import { jsx as _jsx } from "react/jsx-runtime";
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import classNames from 'classnames';
import { useCallback, useRef } from 'react';
import { googleCoordinates } from '../utils/googleCoordinates';
import { toGeoCoordinates } from '../utils/toGeoCoordinates';
// Corners sit above the midpoints, so a corner wins where the two overlap.
const HANDLE_Z_INDEX = { vertex: 2, midpoint: 1 };
// Releasing the mouse after a drag also fires a click on the handle; a click this soon after a drag is ignored.
const CLICK_AFTER_DRAG_IGNORE_MS = 300;
/**
 * A draggable handle of an editable area: a corner (`vertex`) or the middle of an edge (`midpoint`). The visible
 * dot is small, but the touch area around it is large enough for a finger. One drag ends exactly once: events
 * arriving after the drag ended, and the click fired by releasing the mouse, are ignored.
 */
export default function AreaHandle({ position, color, variant, onDrag, onDragEnd, onClick }) {
    const onDragRef = useRef(onDrag);
    const onDragEndRef = useRef(onDragEnd);
    const onClickRef = useRef(onClick);
    onDragRef.current = onDrag;
    onDragEndRef.current = onDragEnd;
    onClickRef.current = onClick;
    const isDraggingRef = useRef(false);
    const dragEndedAtRef = useRef(0);
    // Stable handlers, so the marker's listeners are not swapped while a drag is in progress.
    const handleDragStart = useCallback(() => {
        isDraggingRef.current = true;
    }, []);
    const handleDrag = useCallback((event) => {
        if (!isDraggingRef.current || !event.latLng)
            return;
        onDragRef.current(toGeoCoordinates(event.latLng));
    }, []);
    const handleDragEnd = useCallback((event) => {
        if (!isDraggingRef.current)
            return;
        isDraggingRef.current = false;
        dragEndedAtRef.current = Date.now();
        if (event.latLng)
            onDragEndRef.current(toGeoCoordinates(event.latLng));
    }, []);
    const handleClick = useCallback(() => {
        if (isDraggingRef.current || Date.now() - dragEndedAtRef.current < CLICK_AFTER_DRAG_IGNORE_MS)
            return;
        onClickRef.current();
    }, []);
    return (_jsx(AdvancedMarker, { position: googleCoordinates(position), draggable: true, anchorLeft: "-50%", anchorTop: "-50%", zIndex: HANDLE_Z_INDEX[variant], onDragStart: handleDragStart, onDrag: handleDrag, onDragEnd: handleDragEnd, onClick: handleClick, children: _jsx("div", { className: "flex h-12 w-12 cursor-grab items-center justify-center", children: _jsx("div", { className: classNames('rounded-full border-white shadow-md', variant === 'vertex' ? 'h-4 w-4 border-2' : 'h-2.5 w-2.5 border-2 opacity-80'), style: { backgroundColor: color } }) }) }));
}
